import { useQueryState } from "nuqs"
import { DateTime } from "luxon"
import { getDateRange, formatRangeLabel } from "../utils/datetime"

export const CUSTOM_RANGE = "Custom"
export const ALL_TIME = "All"

const BASE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "This Year",
]

// Only quarters that have started this year get a preset — no "Q4 2026"
// showing up in September. Recomputed per render so it keeps up as the
// current month/year change.
const getElapsedQuarterLabels = (now = DateTime.now()) => {
  const currentQuarter = Math.ceil(now.month / 3)
  return Array.from(
    { length: currentQuarter },
    (_, index) => `Q${index + 1} ${now.year}`
  )
}

/*
  Backs the shared DateRangeControl popover (presets + a calendar for a
  custom range) from a single "dateRange"/"from"/"to" URL state, so every
  page using it stays in sync on what's selectable and how it resolves to
  actual ISO bounds. `includeAllTime` opts a page into an extra "All" preset
  that resolves to an open date filter — getDateRange has no case for it,
  since "no date filter" isn't a range.
*/
export const useDateRangeFilter = ({
  defaultRange = "This Month",
  includeAllTime = false,
} = {}) => {
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: defaultRange,
  })
  const [customFrom, setCustomFrom] = useQueryState("from", {
    defaultValue: "",
  })
  const [customTo, setCustomTo] = useQueryState("to", {
    defaultValue: "",
  })

  const quarterLabels = getElapsedQuarterLabels()
  const currentQuarterLabel = quarterLabels[quarterLabels.length - 1]
  const dateRangeValues = [
    ...BASE_RANGE_OPTIONS,
    ...quarterLabels,
    ...(includeAllTime ? [ALL_TIME] : []),
  ]
  // Labels the running quarter distinctly so it doesn't read as just another
  // completed quarter in the list — the value passed to getDateRange stays
  // bare (e.g. "Q3 2026") so the URL and its regex parsing are unaffected.
  const presetOptions = dateRangeValues.map((value) => ({
    value,
    label: value === currentQuarterLabel ? `${value} (Current)` : value,
  }))

  const isCustomRange = dateRange === CUSTOM_RANGE
  const isAllTime = includeAllTime && dateRange === ALL_TIME

  // A hand-edited URL can carry a range name that getDateRange would throw
  // on, or one this page didn't opt into (e.g. "All" without includeAllTime).
  const resolveNamedRange = (range) =>
    dateRangeValues.includes(range) ? range : defaultRange

  const getDateBounds = () => {
    if (isCustomRange) return { start: customFrom || "", end: customTo || "" }
    if (isAllTime) return { start: "", end: "" }
    const interval = getDateRange(resolveNamedRange(dateRange))
    return { start: interval.start.toISODate(), end: interval.end.toISODate() }
  }

  const { start, end } = getDateBounds()
  const rangeLabel = formatRangeLabel(start, end)

  const handleSelectPreset = (range) => setDateRange(range)

  const handleApplyCustom = (from, to) => {
    setCustomFrom(from)
    setCustomTo(to)
    setDateRange(CUSTOM_RANGE)
  }

  // Seeds the custom calendar from whatever range is already on screen, so it
  // opens on something meaningful instead of blank. "All" has no interval of
  // its own to seed from, so it falls back to the page's default range.
  const getCustomSeed = () => {
    if (isCustomRange) return [customFrom, customTo]
    const named = isAllTime ? defaultRange : resolveNamedRange(dateRange)
    const interval = getDateRange(named)
    return [interval.start.toISODate(), interval.end.toISODate()]
  }

  const handleResetFilters = () => {
    setDateRange(defaultRange)
    setCustomFrom("")
    setCustomTo("")
  }

  const triggerLabel =
    isCustomRange && rangeLabel
      ? rangeLabel
      : isCustomRange
        ? CUSTOM_RANGE
        : dateRange

  return {
    dateRange,
    customFrom,
    customTo,
    isCustomRange,
    start,
    end,
    rangeLabel,
    triggerLabel,
    presetOptions,
    getCustomSeed,
    handleSelectPreset,
    handleApplyCustom,
    handleResetFilters,
  }
}
